import React, { useEffect, useState } from 'react';
import { useDataProvider, useRecordContext } from 'react-admin';
import { useFeatureGateContext } from 'providers/featureGateContext';

interface AssignmentRecord {
  id?: number | string;
  root_assignment_id?: number | string;
  original_assignment_id?: number | string;
}

interface DisableW2MarkupsFeatureGateContextProps {
  overrideAssignmentID?: number | string;
}

const DisableW2MarkupsFeatureGateContext = ({
  overrideAssignmentID,
}: DisableW2MarkupsFeatureGateContextProps): React.ReactElement | null => {
  const record = useRecordContext<AssignmentRecord>();
  const dataProvider = useDataProvider();
  const { setCustomIDs, statsigUser } = useFeatureGateContext();
  const [overrideRootAssignmentID, setOverrideRootAssignmentID] = useState<
    number | string | undefined
  >(undefined);
  const recordRootAssignmentID = record?.root_assignment_id;

  useEffect(() => {
    let mounted = true;
    if (!overrideAssignmentID || overrideAssignmentID === recordRootAssignmentID) {
      setOverrideRootAssignmentID(undefined);
      return;
    }
    // Fetch the root_assignment_id for the user provided overrideAssignmentID
    (async () => {
      try {
        const { data } = await dataProvider.getOne('Assignment', { id: overrideAssignmentID });
        if (!mounted) {
          return;
        }
        setOverrideRootAssignmentID(data?.root_assignment_id);
      } catch (error) {
        if (!mounted) {
          return;
        }
        setOverrideRootAssignmentID(undefined);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [overrideAssignmentID, dataProvider, recordRootAssignmentID]);

  // Prefer override root, otherwise use record root
  const rootAssignmentID = overrideRootAssignmentID ?? recordRootAssignmentID;
  // Fallback to record id if root_assignment_id is not available
  const assignmentID = rootAssignmentID ?? record?.id;
  const currentAssignmentID = statsigUser?.customIDs?.assignment_id;

  useEffect(() => {
    if (!setCustomIDs) {
      return;
    }

    const nextAssignmentID = !!assignmentID ? String(assignmentID) : undefined;

    if (currentAssignmentID === nextAssignmentID) {
      return;
    }

    setCustomIDs(nextAssignmentID ? { assignment_id: nextAssignmentID } : undefined);
  }, [assignmentID, currentAssignmentID, setCustomIDs]);

  return null;
};

export default DisableW2MarkupsFeatureGateContext;
